<template>
    <v-alert
        v-if="showAlert"
        :type="alertType"
        :class="[
            'mx-auto',
            'text-center',
            'bg-' + (alertType === 'success' ? 'green-lighten-4' : 'red-lighten-4')
        ]"
        style="position: fixed; top: 20px; left: 0; right: 0; z-index: 9999; max-width: 600px;"
        elevation="2"
        prominent
    >
        {{ alertMessage }}
    </v-alert>
    <v-container fluid>
        <v-card class="pa-4" elevation="2">
            <v-row>
                <v-col class="headline mb-4">
                    <h2>Almacenes</h2>
                </v-col>
                <v-col cols="12" sm="6" style="text-align: right">
                    <v-btn color="primary" :loading="isImporting" :disabled="isImporting" @click="importWarehouses">
                        <template #loader>
                            <v-progress-circular indeterminate size="20" color="white" />
                        </template>
                        Importar de Odoo
                    </v-btn>

                    <v-btn style="margin-left: 10px" color="primary" :loading="isExporting" :disabled="isExporting" @click="exportWarehouses">
                        <template #loader>
                            <v-progress-circular indeterminate size="20" color="white" />
                        </template>
                        Exportar Excel
                    </v-btn>

                    <v-btn style="margin-left: 10px"  @click="$router.back()">Volver</v-btn>
                </v-col>
            </v-row>

            <!-- Filtres -->
            <v-row class="mb-4" dense>
                <v-col cols="12" sm="4">
                    <v-text-field
                        v-model="filters.name"
                        label="Filtrar por nombre"
                        dense
                        clearable
                    />
                </v-col>

                <v-col cols="12" sm="4">
                    <v-text-field
                        v-model="filters.address"
                        label="Filtrar por dirección"
                        dense
                        clearable
                    />
                </v-col>

                <v-col cols="12" sm="2">
                    <v-text-field v-model="filters.id_odoo" label="Filtrar por ID" dense clearable />
                </v-col>

                <v-col cols="12" sm="2">
                    <v-select
                        v-model="filters.active"
                        :items="activeOptions"
                        item-title="text"
                        item-value="value"
                        label="Activo"
                        dense
                        clearable
                    />
                </v-col>
            </v-row>

            <v-data-table
                :items="filteredWarehouses"
                :headers="headers"
                class="elevation-1"
                no-data-text="No hay almacenes disponibles"
            >
                <!-- dins del v-data-table -->
                <template #item.active="{ item }">
                    <v-tooltip :text="normalizeBool(item.active) ? 'Desactivar' : 'Activar'">
                        <template #activator="{ props }">
                            <v-btn
                                v-bind="props"
                                icon
                                variant="text"
                                :loading="savingRowId === item.id"
                                :color="normalizeBool(item.active) ? 'green' : 'red'"
                                :disabled="savingRowId === item.id"
                                @click="toggleActive(item)"
                                aria-label="Canviar estat actiu"
                            >
                                <v-icon>
                                    {{ normalizeBool(item.active) ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                </v-icon>
                            </v-btn>
                        </template>
                    </v-tooltip>
                </template>

                <template #item.actions="{ item }">
                    <div class="d-flex justify-end">

                        <v-tooltip text="Eliminar">
                            <template #activator="{ props }">
                                <v-btn
                                    icon
                                    class="ma-1"
                                    v-bind="props"
                                    @click="deleteWarehouses(item.id)"
                                >
                                    <v-icon>mdi-delete</v-icon>
                                </v-btn>
                            </template>
                        </v-tooltip>
                    </div>
                </template>
            </v-data-table>
        </v-card>
    </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const router = useRouter()

import { useAlert } from '@/composables/useAlert'

const {
    alertMessage,
    alertType,
    showAlert,
    showAlertMessage
} = useAlert()

const isImporting = ref(false)
const isExporting = ref(false)
const savingRowId = ref(null) // <-- nova: control de loader per fila

const filters = ref({
    name: '',
    address: '',
    id_odoo: '',
    active: null, // null = Todos
})

const activeOptions = [
    { text: 'Todos', value: null },
    { text: 'Sí', value: true },
    { text: 'No', value: false }
]

const normalizeBool = v => v === true || v === 1 || v === '1'

const lower = v => (v ?? '').toString().toLowerCase()

const filteredWarehouses = computed(() => {
    return warehouses.value.filter(w => {
        const matchName    = !filters.value.name    || lower(w.name).includes(lower(filters.value.name))
        const matchAddress = !filters.value.address || lower(w.address).includes(lower(filters.value.address))
        const idStr        = (w.id_odoo ?? '').toString()
        const matchIdOdoo  = !filters.value.id_odoo || idStr.includes(filters.value.id_odoo.toString())

        const matchActive  =
            filters.value.active === null ||
            normalizeBool(w.active) === Boolean(filters.value.active)

        return matchName && matchAddress && matchIdOdoo && matchActive
    })
})


const warehouses = ref([])
const headers = [
    { title: 'Nombre', value: 'name' , sortable: true},
    { title: 'Dirección', value: 'address' , sortable: true},
    { title: 'Id Odoo', value: 'id_odoo' , sortable: true},
    { title: 'Activo', key: 'active' , sortable: true},
    { title: 'Acciones', key: 'actions', sortable: false }
]

const fetchWarehouses = async () => {
    try {
        const {data} = await axios.get('/api/admin/warehouses', {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })
        warehouses.value = data
    }
    catch (e) {
        console.error('Error loading warehouses:', e)
        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }
        showAlertMessage('Error cargando los almacenes.', 'error')
    }
}

const deleteWarehouses = async (id) => {
    if (!confirm('¿Estás seguro que quieres eliminar este almacén?')) return

    try {
        await axios.delete(`/api/admin/warehouses/${id}`, {
            withCredentials: true,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        })

        await fetchWarehouses()
    } catch (e) {
        console.error('Error eliminando el almacén:', e)

        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }

        showAlertMessage('Error eliminando al almacén.', 'error')
    }
}

const importWarehouses = async () => {
    isImporting.value = true
    showAlertMessage('Importando datos de Odoo...', 'success')
    try {
        await axios.post('/api/admin/warehouses/import', {}, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })
        await fetchWarehouses()
        showAlertMessage('Importación completada correctamente.', 'success')
    } catch (e) {
        console.error('Error importando proveedores:', e)
        showAlertMessage('Error importando los proveedores.', 'error')
    } finally {
        isImporting.value = false
    }
}

const exportWarehouses= async () => {
    isExporting.value = true
    showAlertMessage('Exportando almacenes...', 'success')
    try {
        const response = await axios.get(
            `/api/admin/warehouses/export`,
            {
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
            }
        )
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const now = new Date()
        const timestamp = now.toISOString().replace(/[-:.TZ]/g, '').slice(0, 14)
        link.download = `warehouses_${timestamp}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        showAlertMessage('Exportación completada correctamente.', 'success')
    } catch (e) {
        console.error('Error exportant magatzems:', e)
        showAlertMessage('Error exportando almacenes.', 'error')
    } finally {
        isExporting.value = false
    }
}

const toggleActive = async (item) => {
    // Evita doble clic mentre desa
    if (savingRowId.value === item.id) return

    const newActive = !normalizeBool(item.active)
    const payload = {
        name: item.name,
        address: item.address,
        id_odoo: item.id_odoo,
        active: newActive
    }

    savingRowId.value = item.id
    const headers = {
        withCredentials: true,
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    }

    try {
        // Optimistic update (actualitzem UI de seguida)
        const prev = item.active
        item.active = newActive

        await axios.put(`/api/admin/warehouses/${item.id}`, payload, headers)

        showAlertMessage('Estado actualizado correctamente.', 'success')
    } catch (e) {
        // Reverteix si falla
        item.active = !newActive
        console.error('No se pudo actualizar el estado:', e)
        showAlertMessage('No se pudo actualizar el estado.', 'error')

        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }
    } finally {
        savingRowId.value = null
    }
}

onMounted(fetchWarehouses)
</script>
